<template>
  <!-- ════════ Touch / coarse-pointer: fixed game zone over a docked keyboard ════════ -->
  <div
    v-if="isTouch"
    class="rd-screen rd-screen--touch"
    :class="{ 'timer-crisis-vignette': timeRemaining <= 5000 }"
    data-testid="timer-container"
  >
    <div class="rd-zone">
      <div class="rd-card">
        <section class="rd-hero-wrap" :aria-label="STRINGS.aria.labels.timer">
          <TimerDisplayHero
            variant="card"
            :time-remaining="timeRemaining"
            :is-active="isActive"
            :is-paused="isPaused"
            :server-sync="serverSynced"
          />
        </section>

        <section :aria-label="STRINGS.aria.labels.availableLetters">
          <SelectedLettersGrid :letters="selectedLetters" :urgent="isUrgent" double-row />
        </section>

        <section class="rd-rec-status" :aria-label="STRINGS.aria.labels.playerStatus">
          <StatusChip
            :name="STRINGS.status.player.youShort"
            :state="currentPlayerStatus"
            :submitted="wordSubmitted"
          />
          <span class="rd-pill-div" aria-hidden="true" />
          <StatusChip :name="opponentName" :state="opponentStatus" :submitted="opponentSubmitted" />
        </section>
      </div>
    </div>

    <!-- Dock detaches from the card to sit directly above the keyboard. -->
    <div class="rd-dock rd-dock--detached">
      <WordInput
        mode="display"
        :value="currentWord"
        :disabled="!isActive || wordSubmitted"
        :submitted="wordSubmitted"
        :available-letters="selectedLetters"
      />
      <button
        type="button"
        class="rd-submit"
        :class="{ 'rd-submit--ok': wordSubmitted }"
        :disabled="!canSubmitWord"
        :aria-label="submitAriaLabel"
        @click="onSubmit"
        data-testid="submit-button"
      >
        <component :is="wordSubmitted ? IconCheck : IconArrow" />
      </button>
    </div>

    <div class="rd-kb-slot">
      <RoundKeyboard :disabled="!isActive || wordSubmitted" @key="onKey" @delete="onDelete" />
    </div>

    <ConnectionErrorOverlay
      :is-visible="isConnectionErrorVisible"
      :error-type="connectionErrorType"
      :reconnection-state="reconnectionState"
      :timer-active="isActive"
      :time-remaining="timeRemaining / 1000"
      @retry-connection="emit('retryConnection')"
    />
  </div>

  <!-- ════════ Desktop / fine-pointer: same arena card centered in the void ════════ -->
  <div
    v-else
    class="rd-screen rd-screen--desk"
    :class="{ 'timer-crisis-vignette': timeRemaining <= 5000 }"
    data-testid="timer-container"
  >
    <div class="rd-card rd-card--desk">
      <section class="rd-hero-wrap" :aria-label="STRINGS.aria.labels.timer">
        <TimerDisplayHero
          variant="desktop"
          :time-remaining="timeRemaining"
          :is-active="isActive"
          :is-paused="isPaused"
          :server-sync="serverSynced"
        />
      </section>

      <section :aria-label="STRINGS.aria.labels.availableLetters">
        <SelectedLettersGrid :letters="selectedLetters" :urgent="isUrgent" double-row />
      </section>

      <section class="rd-rec-status" :aria-label="STRINGS.aria.labels.playerStatus">
        <StatusChip
          :name="STRINGS.status.player.youShort"
          :state="currentPlayerStatus"
          :submitted="wordSubmitted"
        />
        <span class="rd-pill-div" aria-hidden="true" />
        <StatusChip :name="opponentName" :state="opponentStatus" :submitted="opponentSubmitted" />
      </section>

      <div class="rd-dock">
        <WordInput
          ref="wordInputRef"
          mode="native"
          :value="currentWord"
          :disabled="!isActive || wordSubmitted"
          :submitted="wordSubmitted"
          :available-letters="selectedLetters"
          :auto-focus="isActive"
          @update:value="emit('update:currentWord', $event)"
          @submit="onSubmit"
        />
        <button
          type="button"
          class="rd-submit rd-submit--desk"
          :class="{ 'rd-submit--ok': wordSubmitted }"
          :disabled="!canSubmitWord"
          :aria-label="submitAriaLabel"
          @click="onSubmit"
          data-testid="submit-button"
        >
          {{ submitButtonText }}
        </button>
      </div>

      <p class="rd-desk-hint">{{ STRINGS.ui.input.desktopHint }}</p>
    </div>

    <ConnectionErrorOverlay
      :is-visible="isConnectionErrorVisible"
      :error-type="connectionErrorType"
      :reconnection-state="reconnectionState"
      :timer-active="isActive"
      :time-remaining="timeRemaining / 1000"
      @retry-connection="emit('retryConnection')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, h } from 'vue';
import SelectedLettersGrid from './SelectedLettersGrid.vue';
import TimerDisplayHero from './TimerDisplayHero.vue';
import WordInput from './WordInput.vue';
import RoundKeyboard from './RoundKeyboard.vue';
import StatusChip from './StatusChip.vue';
import ConnectionErrorOverlay from './ConnectionErrorOverlay.vue';
import type { ConnectionErrorType } from './ConnectionErrorOverlay.vue';
import type { Letter } from '@/types/generated';
import STRINGS from '@/constants/strings';
import { useInputMode } from '@/composables/useInputMode';

interface Props {
  // Timer state
  timeRemaining: number;
  isActive: boolean;
  isPaused: boolean;
  serverSynced: boolean;
  isUrgent: boolean;
  displaySeconds: number;

  // Game state
  selectedLetters: Letter[];
  wordSubmitted: boolean;
  opponentSubmitted: boolean;

  // Word submission state
  currentWord: string;
  submissionType: 'manual' | 'auto-word' | 'auto-empty';
  canSubmitWord: boolean;
  submitButtonText: string;
  currentPlayerStatus: string;
  opponentStatus: string;
  opponentName: string;

  // Connection error state
  isConnectionErrorVisible: boolean;
  connectionErrorType: ConnectionErrorType;
  reconnectionState: {
    isReconnecting: boolean;
    attempt: number;
    maxAttempts: number;
  };
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:currentWord': [value: string];
  submitWord: [];
  retryConnection: [];
}>();

const { isTouch } = useInputMode();
const wordInputRef = ref<InstanceType<typeof WordInput>>();

const submitAriaLabel = computed(() =>
  STRINGS.templates.submitAriaLabel(
    props.displaySeconds,
    props.wordSubmitted,
    props.canSubmitWord,
    props.submissionType !== 'manual'
  )
);

// ── Word entry ────────────────────────────────────────────────────────────
// The custom keyboard (and any hardware keyboard, on touch) drives the word
// through the same `update:currentWord` / `submitWord` contract the native
// input uses, so the upstream submission flow is unchanged.
const maxLength = computed(() => props.selectedLetters.length || 10);

const onKey = (char: string) => {
  if (!props.isActive || props.wordSubmitted) return;
  const next = (props.currentWord + char.toUpperCase()).slice(0, maxLength.value);
  emit('update:currentWord', next);
};

const onDelete = () => {
  if (!props.isActive || props.wordSubmitted) return;
  emit('update:currentWord', props.currentWord.slice(0, -1));
};

const onSubmit = () => {
  // Upstream (useGameSnapshot.handleWordSubmit) guards empty/inactive submits.
  emit('submitWord');
};

// Hardware-keyboard support on touch devices (e.g. an iPad with a Bluetooth
// keyboard, or switch-access tooling): there is no focused native input to
// receive keystrokes, so listen at the window and route a–z / Backspace /
// Enter through the same handlers. Ignored when focus is on an interactive
// control so it never double-fires with a button's own Enter/Space.
const handleHardwareKey = (event: KeyboardEvent) => {
  if (!isTouch.value) return;
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  const target = event.target;
  if (
    target instanceof HTMLElement &&
    target.closest('button, [role="button"], input, textarea, select')
  ) {
    return;
  }

  if (/^[a-zA-Z]$/.test(event.key)) {
    event.preventDefault();
    onKey(event.key.toLowerCase());
  } else if (event.key === 'Backspace') {
    event.preventDefault();
    onDelete();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    onSubmit();
  }
};

onMounted(() => window.addEventListener('keydown', handleHardwareKey));
onBeforeUnmount(() => window.removeEventListener('keydown', handleHardwareKey));

// Inline icons for the gold submit button (up-arrow → check on submit).
const IconArrow = () =>
  h('svg', { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': 'true' }, [
    h('path', {
      d: 'M12 19V5M5 12l7-7 7 7',
      stroke: 'currentColor',
      'stroke-width': 2.6,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }),
  ]);
const IconCheck = () =>
  h('svg', { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': 'true' }, [
    h('path', {
      d: 'M5 13l4 4 10-11',
      stroke: 'currentColor',
      'stroke-width': 2.6,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }),
  ]);
</script>

<style scoped>
/* ── Touch screen: fixed zone above a permanently-docked keyboard ─────────── */
.rd-screen--touch {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.rd-zone {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* iOS: start below the Dynamic Island / status bar (env() resolves to the
     real top inset on notch devices); 22px on everything else. */
  padding: max(env(safe-area-inset-top, 0px), 22px) 18px 10px;
  /* Safety net for very short viewports: the card may scroll within the zone,
     but the dock + keyboard never move and the input never hides. */
  overflow-y: auto;
}

/* The arena card fills the zone with its content centered, so the round screen
   reads as a framed arena rather than a small card floating in a void. */
.rd-zone > .rd-card {
  flex: 1 1 auto;
  justify-content: center;
}

.rd-kb-slot {
  flex-shrink: 0;
  overflow: hidden;
  background: #0a0a0c;
}

/* ── Arena card (shared) ──────────────────────────────────────────────────── */
.rd-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: var(--color-arena-surface);
  border: 1px solid var(--color-arena-border);
  border-radius: 16px;
  padding: 20px 16px 22px;
}

.rd-hero-wrap {
  min-height: 104px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rd-card--desk .rd-hero-wrap {
  min-height: 110px;
}

/* ── Input + inline submit dock ───────────────────────────────────────────── */
.rd-dock {
  display: flex;
  gap: 10px;
  align-items: stretch;
  flex-shrink: 0;
}

/* Detached dock (touch): pinned between the zone and the docked keyboard. */
.rd-dock--detached {
  padding: 0 18px 12px;
}

.rd-submit {
  flex: 0 0 auto;
  width: 60px;
  border: none;
  cursor: pointer;
  border-radius: 10px;
  background: linear-gradient(180deg, var(--color-primary-500), var(--color-primary-600));
  color: var(--color-arena-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.15s,
    filter 0.2s,
    background 0.3s;
}

.rd-submit:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.rd-submit:active:not(:disabled) {
  transform: scale(0.96);
}

.rd-submit:disabled {
  opacity: 0.4;
  cursor: default;
}

.rd-submit--ok {
  background: var(--color-energy-success);
  color: #04130d;
  opacity: 1;
}

.rd-submit:focus-visible {
  outline: 3px solid var(--color-primary-600);
  outline-offset: 2px;
}

/* ── Status chips row ─────────────────────────────────────────────────────── */
.rd-rec-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.rd-pill-div {
  width: 1px;
  height: 16px;
  background: var(--color-arena-border);
}

/* ── Desktop screen: card centered in the void, physical keyboard ─────────── */
.rd-screen--desk {
  min-height: 100vh;
  background: var(--color-arena-void);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.rd-card--desk {
  width: 560px;
  max-width: 100%;
  gap: 24px;
  padding: 36px 40px 30px;
  border-radius: 20px;
}

.rd-submit--desk {
  width: auto;
  padding: 0 28px;
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.rd-desk-hint {
  margin: 0;
  text-align: center;
  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--color-arena-text-muted);
}

@media (prefers-reduced-motion: reduce) {
  .rd-submit {
    transition: none;
  }
  .rd-submit:hover:not(:disabled),
  .rd-submit:active:not(:disabled) {
    transform: none;
  }
}
</style>
